import CustomImage from "@components/custom-image";
import Spacer from "@components/Spacer";
import BackButton from "@components/back-button";
import IconButton from "@components/icon-button";
import AccountIcon from "@components/icons/account-icon";
import CartIcon from "@components/icons/cart-icon";
import MenuIcon2 from "@components/icons/menu-icon2";
import ProductSearchbar from "@components/product-searchbar";
import { useCartBadge } from "@features/cart/shopping-cart-store";
import { useVacancyCount } from "@features/vacancy/use-vacancies";
import useCustomWindowDimensions from "@hooks/use-custom-window-dimensions";
import useShowRecipes from "@hooks/use-show-recipes";
import { useAuth } from "@provider/context-auth";
import { Container, Text, View } from "dripsy";
import { Platform, Pressable } from "react-native";
import * as React from "react";
import type { BottomTabOverrideProps } from "react-native-ridge-navigation";
import { BottomTabLink, useNavigation } from "react-native-ridge-navigation";
import { NavigationContext } from "navigation-react";
import HeaderItem from "./header-item";
import { BottomRoots } from "../Navigator";
import routes from "../NavigatorRoutes";
import AssistantTriggerButton from "@features/assistant/assistant-trigger-button";
import { ASSISTANT_DOCK_BREAKPOINT, ASSISTANT_DOCK_WIDTH } from "@features/assistant/assistant-panel";
import { useAssistant } from "@features/assistant/assistant-provider";
import { useAssistantEnabled } from "@features/assistant/use-assistant-enabled";

const HEADER_ITEMS_ALL = [
  { id: "/", label: "Home", route: routes.HomeScreen },
  { id: "/boodschappen-doen", label: "Boodschappen doen", route: routes.CategoryScreen },
  { id: "/recepten", label: "Recepten", route: routes.RecipesScreen },
  { id: "/over-ons", label: "Over ons", route: routes.AboutScreen, params: { anchor: "-" } },
  { id: "/webwinkel", label: "Onze principes", route: routes.WebshopScreen, params: { anchor: "-" } },
  { id: "/contact", label: "Contact", route: routes.ContactScreen },
];

function HeaderWeb({ orientation, originalBottomTabs, children }: BottomTabOverrideProps) {
  const { isOpen } = useAssistant();
  const isAssistantEnabled = useAssistantEnabled();
  const { width } = useCustomWindowDimensions();
  const hasAssistantDock = isAssistantEnabled && Platform.OS === "web" && isOpen && width >= ASSISTANT_DOCK_BREAKPOINT;

  return (
    <View
      sx={{
        flex: 1,
        ...(hasAssistantDock ? { marginRight: ASSISTANT_DOCK_WIDTH } : {}),
      }}
    >
      <Inner />
      <View sx={{ flex: 1 }}>{children}</View>
    </View>
  );
}

const Inner = () => {
  const { stateNavigator, state } = React.useContext(NavigationContext);
  const crumbs = stateNavigator?.stateContext.crumbs;
  const wasSearching = crumbs.some((crumb) => crumb.url.includes("/zoeken"));
  const isOnSearchPage = state.route.includes("/zoeken");

  const { push } = useNavigation();
  const showRecipes = useShowRecipes();
  const HEADER_ITEMS = (() => {
    if (showRecipes) {
      return HEADER_ITEMS_ALL;
    }
    return HEADER_ITEMS_ALL.filter((item) => item.id !== "/recepten");
  })();
  // const [defaultSelected, setDefaultSelected] = React.useState(
  //   HEADER_ITEMS.find((item) => partBeforeQuery.startsWith(item.id)) ?? HEADER_ITEMS[0]
  // );
  const { width } = useCustomWindowDimensions();
  const isAssistantEnabled = useAssistantEnabled();

  const profile = useAuth()?.profile;
  const vacancyCount = useVacancyCount();

  const isBigScreen = width > 1100;
  return (
    <View
      sx={{
        top: 0,
        zIndex: 100,
        backgroundColor: "$secondaryBackground",
        borderBottomWidth: 1,
        borderStyle: "solid",
        borderColor: "$lightGrey",
        paddingTop: "env(safe-area-inset-top)",
        paddingLeft: isBigScreen ? 18 : "env(safe-area-inset-left)",
        paddingRight: isBigScreen ? 18 : "env(safe-area-inset-right)",
        alignItems: "center",
        flexDirection: "row",
      }}
    >
      <Container variant="large">
        <View
          style={{
            gap: 10,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <View
            style={
              isBigScreen
                ? { display: "none" }
                : {
                    flexDirection: "row",
                  }
            }
          >
            <View sx={{ alignItems: "center", position: "relative" }}>
              <select
                style={{
                  position: "absolute",
                  opacity: 0,
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
                value=""
                onChange={(event) => {
                  const selected = HEADER_ITEMS.find((item) => item.id === event.target.value);
                  if (selected) {
                    push(selected.route, selected.params || {});
                  }
                }}
              >
                <option value="" disabled>
                  Menu
                </option>
                {HEADER_ITEMS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.id === "/over-ons" && vacancyCount ? `${item.label} (${vacancyCount})` : item.label}
                  </option>
                ))}
              </select>
              <MenuIcon2 size={16} color="$textSecondary" />
              <Text
                sx={{
                  fontWeight: "500",
                  pt: 2,
                  fontSize: 10,
                  color: "$textSecondary",
                }}
              >
                Menu
              </Text>
            </View>
          </View>
          <View
            style={{
              width: 138,
              height: 34,
            }}
          >
            <BottomTabLink to={BottomRoots.Home} params={{}}>
              {(props) => (
                <Pressable {...(props as any)}>
                  <CustomImage
                    source={require("../assets/logo.png")}
                    sx={{ width: isBigScreen ? 138 * 1.3 : 138 * 0.9, height: isBigScreen ? 34 * 1.3 : 34 * 0.9 }}
                    alt="logo"
                    priority
                  />
                </Pressable>
              )}
            </BottomTabLink>
          </View>
          <View sx={isBigScreen ? { flexDirection: "row", gap: "$2", alignItems: "center" } : { display: "none" }}>
            <HeaderItem to={BottomRoots.Products} title="Boodschappen doen" highlighted />
            {showRecipes ? <HeaderItem to={BottomRoots.Recipes} title="Recepten" /> : null}
            <HeaderItem to={BottomRoots.AboutUs} title="Over ons" badge={vacancyCount} />
            <HeaderItem to={BottomRoots.Webshop} title="Werking" />
            <HeaderItem to={BottomRoots.Contact} title="Contact" />
          </View>
          <View
            style={{
              display: "flex",
              gap: 12,
              flexDirection: "row",
            }}
          >
            {isAssistantEnabled ? <AssistantTriggerButton /> : null}
            <BottomTabLink to={BottomRoots.Account} params={{}}>
              {(props) => (
                <IconButton {...props} hitslop={15} backgroundColor="$secondaryBackground">
                  <AccountIcon size={20} />
                  <Text
                    numberOfLines={1}
                    sx={{
                      fontWeight: "500",
                      pt: "$1",
                      fontSize: [10, 11, 13],
                      color: "$textSecondary",
                      maxWidth: [45, 56, 75],
                    }}
                  >
                    {profile?.first_name || "Inloggen"}
                  </Text>
                </IconButton>
              )}
            </BottomTabLink>
            <BottomTabLink to={BottomRoots.Cart} params={{}}>
              {(props) => (
                <IconButton {...props} hitslop={15} backgroundColor="$secondaryBackground">
                  <CartIcon size={20} />
                  <CartBadge />

                  <Text sx={{ fontWeight: "500", pt: "$1", fontSize: [10, 11, 13], color: "$textSecondary" }}>
                    Mand
                  </Text>
                </IconButton>
              )}
            </BottomTabLink>
          </View>
        </View>

        <Spacer size="$4" />
        <View
          sx={{
            flexDirection: "row",
            flex: 1,
            position: "absolute",
            top: isBigScreen ? 60 : 50,
            left: 12,
            right: 12,
          }}
        >
          <Container sx={{ flexDirection: "row", px: 0 }}>
            <BackButton withText backgroundColor="$background" />
            <Spacer horizontal />
            <View sx={{ flex: 1 }}>
              <ProductSearchbar placeholder="Zoek je boodschappen" isButton={!isOnSearchPage} isPop={wasSearching} />
            </View>
          </Container>
        </View>
      </Container>
    </View>
  );
};

const CartBadge = () => {
  const totalAmount = useCartBadge();
  const text = totalAmount > 99 ? "99+" : `${totalAmount}`;
  const totalAmountLength = text.length;

  const width = Math.max(totalAmountLength * 9 + 12, 25);
  return totalAmount > 0 ? (
    <View
      sx={{
        position: "absolute",
        right: -width + 20,
        top: 4,
        backgroundColor: "$primary",
        borderRadius: 360,
        px: 6,
        py: 2,
        minHeight: 25,
        justifyContent: "center",
        alignItems: "center",
        borderWidth: 2,
        borderColor: "$secondaryBackground",
        width,
      }}
    >
      <Text sx={{ color: "$background", fontSize: 12, fontWeight: "bold" }}>{text}</Text>
    </View>
  ) : null;
};

export default HeaderWeb;
